import { LitElement, css, html, nothing } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { getGlobalStyleSheets } from '../../global-styles';

import '../button';
import '../placeholders';

import CloseButtonImg from '@kpmg-uk/tti-design-styles/dist/assets/images/x-icon.svg';
import LogoImg from '@kpmg-uk/tti-design-styles/dist/assets/images/logo.svg';
import { animations } from '../animations';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import { AtlSidebarStripeProps, SidebarLogo } from './props';

import * as TagNames from './sidebar.tag-names';
import {
   PrimaryBrandClickEvent,
   SecondaryBrandClickEvent,
   ToggleSidebarEvent,
} from './events';

@customElement(TagNames.SidebarStripe)
export class AtlSidebarStripe
   extends LitElement
   implements AtlSidebarStripeProps
{
   declare appTitle?: string;
   declare primaryLogo?: SidebarLogo;
   declare secondaryLogo?: SidebarLogo;
   declare showPlaceholder?: boolean;
   declare appVersion?: string;
   declare condensed?: boolean;
   declare expandSubItemsOnFirstLoad?: boolean;
   declare sidebarClassNames?: string;

   declare _show: boolean;
   declare _isToggling?: boolean;

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         .sidebar-stripe::-webkit-scrollbar {
            width: 5px;
         }

         .sidebar-stripe::-webkit-scrollbar-track {
            background: #d5d5d5;
         }

         .sidebar-stripe::-webkit-scrollbar-thumb {
            background-color: #6d6d6d;
            border-radius: 20px;
            border: 0;
         }

         atlantis-icon[name='chevron-down'],
         atlantis-icon[name='chevron-up'] {
            justify-content: flex-end !important;
         }

         atlantis-icon[name='chevron-down']::part(icon),
         atlantis-icon[name='chevron-up']::part(icon) {
            font-size: 12px !important;
         }
      `,
   ];

   static properties = {
      appTitle: { type: String },
      primaryLogo: { type: Object },
      secondaryLogo: { type: Object },
      showPlaceholder: { type: Boolean },
      appVersion: { type: String },
      condensed: { type: Boolean },
      expandSubItemsOnFirstLoad: { type: Boolean },
      sidebarClassNames: { attribute: 'sidebar-class-names', type: String },

      _show: { type: Boolean, state: true },
      _isToggling: { type: Boolean, state: true },
      _activeIndex: { type: Number, state: true },
   };

   constructor() {
      super();
      this.condensed = false;
      this._show = true;
      this._isToggling = false;
   }

   @query('.sidebar-overlay')
   private _overlayElem!: HTMLElement;

   connectedCallback(): void {
      super.connectedCallback();
      window.addEventListener('resize', this._handleResize);
      window.addEventListener(
         ToggleSidebarEvent.EVENT_NAME,
         this._handleToggle
      );

      this._show = window.innerWidth <= 991 ? false : true;
   }

   disconnectedCallback(): void {
      window.removeEventListener('resize', this._handleResize);
      window.addEventListener(
         ToggleSidebarEvent.EVENT_NAME,
         this._handleToggle
      );
      super.disconnectedCallback();
   }

   renderBrand() {
      const classNames: { [key: string]: boolean } = { logo: true };
      if (this.primaryLogo?.className) {
         classNames[this.primaryLogo.className] = true;
      }

      return html`
         <div class="sidebar-brand">
            <atlantis-button
               variant="action"
               class="brand-link"
               @click=${this._handlePrimaryBrandClick}
            >
               <img
                  class="${classMap(classNames)}"
                  src="${this.primaryLogo?.src ?? LogoImg}"
                  alt="${this.primaryLogo?.altText ?? 'KPMG logo'}"
               />
            </atlantis-button>
            <span class="app-title">${this.appTitle}</span>
         </div>
      `;
   }

   private renderSideBarItemPlaceholder() {
      return [1, 2, 3].map(
         () => html`
            <li class="sidebar-item">
               <div class="btn btn-link">
                  <atlantis-span-placeholder
                     size="extrasmall"
                     class="icon"
                     glow
                  ></atlantis-span-placeholder>
                  <atlantis-span-placeholder
                     size="medium"
                     class="menu-text label"
                     glow
                  ></atlantis-span-placeholder>
               </div>
            </li>
         `
      );
   }

   private _renderSidebarItems = () => {
      return html`<div class="sidebar-items" aria-label="sidebar">
         ${this.showPlaceholder
            ? html`${this.renderSideBarItemPlaceholder()}`
            : html`<slot></slot>`}
      </div>`;
   };

   renderSecondaryLogo() {
      if (!this.secondaryLogo?.src) return nothing;

      const classNames: { [key: string]: boolean } = { logo: true };
      if (this.secondaryLogo?.className) {
         classNames[this.secondaryLogo.className] = true;
      }

      return html`
         <atlantis-button
            variant="action"
            class="${classMap(classNames)}"
            @click=${this._handleSecondaryBrandClick}
         >
            <img
               class="logo"
               src="${ifDefined(this.secondaryLogo?.src)}"
               alt="${ifDefined(this.secondaryLogo?.altText)}"
               height="35"
               style="width: auto"
            />
         </atlantis-button>
      `;
   }

   render() {
      const classNames: { [key: string]: boolean } = {
         'sidebar-stripe': true,
         active: this._show === true,
         condensed: this.condensed === true,
      };

      if (this.sidebarClassNames) {
         this.sidebarClassNames
            .split(' ')
            .forEach((className) => (classNames[className] = true));
      }

      return html`
         <nav
            class="${classMap(classNames)}"
            role="navigation"
            aria-label="Main Sidebar"
         >
            <button
               type="button"
               class="btn btn-link sidebar-btn-close d-lg-none"
               @click="${this._handleToggle}"
            >
               <img src="${CloseButtonImg}" alt="Close Menu" />
            </button>
            ${this.renderBrand()} ${this._renderSidebarItems()}
            <div
               class="d-flex align-items-end justify-content-center flex-fill py-2 sidebar-footer"
            >
               <div
                  class="d-flex flex-column align-items-center justify-content-center"
               >
                  ${this.renderSecondaryLogo()}
                  ${this.appVersion
                     ? html`<span class="app-version">${this.appVersion}</span>`
                     : nothing}
               </div>
            </div>
         </nav>
         <div
            class="sidebar-overlay"
            @click="${this._handleOverlayClick}"
         ></div>
      `;
   }

   private _handleResize = () => {
      const isMobile = window.innerWidth <= 991 ? true : false;
      if (isMobile && this._show) {
         this._hideSizePanel();
      }

      if (!isMobile) {
         this._show = true;
      }
   };

   private _handleToggle = () => {
      if (!this._isToggling && !this._show) {
         this._showSizePanel();
      } else {
         this._hideSizePanel();
      }
   };

   private _showSizePanel = () => {
      this._isToggling = true;
      this._addOverlay();
      this._show = true;
      this._isToggling = false;
   };

   private _hideSizePanel = () => {
      this._isToggling = true;
      this._removeOverlay();
      this._show = false;
      this._isToggling = false;
   };

   private _addOverlay = () => {
      animations.cssAddFade(this._overlayElem, 0.3);
      animations.cssFadeIn(this._overlayElem);
   };

   private _removeOverlay = () => {
      animations.cssFadeOut(this._overlayElem);
      setTimeout(() => {
         animations.cssRemoveFade(this._overlayElem);
      }, 300);
   };

   private _handleOverlayClick = () => {
      if (!this._isToggling) this._hideSizePanel();
   };

   private _handlePrimaryBrandClick = () =>
      this.dispatchEvent(new PrimaryBrandClickEvent());

   private _handleSecondaryBrandClick = () =>
      this.dispatchEvent(new SecondaryBrandClickEvent());
}
